/**
 * ContactSelector.js — 从 contacts 集合搜索选择联系人，自动填充关联字段
 *
 * 功能：
 * - 输入时搜索 contacts（?search=），展示 full_name / company / title
 * - 如果有 client_id，优先搜索该 client 下的联系人
 * - 选中后回调 onContactSelect(contact) 让父组件填充字段
 * - 仍可手动输入
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSelector({
  value,            // 当前 contact_person 显示值
  contactId,        // 当前选中的 contact.id（如有）
  clientId,         // 当前关联的 client_id，用于筛选该客户的联系人
  onChange,         // (value) => void  — 更新 contact_person 文本
  onContactSelect,  // (contact) => void — 选中完整联系人对象
  onClear,          // () => void       — 清除关联
  placeholder = 'Search or type contact name...',
  testId = 'contact-selector',
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const handleInputChange = (text) => {
    onChange(text);
    setHighlightIndex(-1);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (text.trim().length < 1) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    setSearching(true);

    searchTimer.current = setTimeout(async () => {
      try {
        // If client_id is set, also fetch that client's contacts
        let allResults = [];
        if (clientId) {
          const clientRes = await axios.get(`${API}/clients/${clientId}/contacts`);
          allResults = clientRes.data || [];
        }
        // Also search globally
        const searchRes = await axios.get(`${API}/contacts`, { params: { search: text.trim(), limit: 8 } });
        const searchResults = searchRes.data?.data || [];

        // Merge and deduplicate
        const seen = new Set();
        const merged = [];
        for (const c of [...allResults, ...searchResults]) {
          if (!seen.has(c.id) && c.full_name?.toLowerCase().includes(text.toLowerCase())) {
            seen.add(c.id);
            merged.push(c);
          }
        }
        setResults(merged.slice(0, 10));
      } catch (err) {
        console.error('Contact search failed:', err);
        setResults([]);
      }
      setSearching(false);
    }, 300);
  };

  const handleSelectContact = (contact) => {
    onChange(contact.full_name);
    onContactSelect?.(contact);
    setIsOpen(false);
    setResults([]);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onClear?.();
    setResults([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectContact(results[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Category badge colors
  const categoryColors = {
    client:        { bg: '#dbeafe', color: '#1e40af' },
    supplier:      { bg: '#fef3c7', color: '#92400e' },
    subcontractor: { bg: '#d1fae5', color: '#065f46' },
    consultant:    { bg: '#ede9fe', color: '#5b21b6' },
    internal:      { bg: '#f3f4f6', color: '#374151' },
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => {
              if (value && value.trim().length >= 1 && results.length === 0) {
                handleInputChange(value);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="editable-field w-full pr-8"
            data-testid={testId}
          />
          {searching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c9a84c] animate-spin" />
          )}
        </div>

        {/* Linked contact badge */}
        {contactId && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => navigate(`/contacts/${contactId}`)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors"
              style={{ background: '#1a1a2e', color: '#c9a84c' }}
              title="View contact details"
              data-testid={`${testId}-link`}
            >
              <User className="w-3 h-3" />
              Linked
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg border max-h-64 overflow-y-auto"
          style={{ background: '#f8f7f4', borderColor: 'rgba(201,168,76,0.3)' }}
          data-testid={`${testId}-dropdown`}
        >
          {results.map((contact, i) => {
            const catStyle = categoryColors[contact.category] || categoryColors.client;
            return (
              <div
                key={contact.id}
                className="px-3 py-2.5 cursor-pointer transition-colors border-b last:border-b-0"
                style={{
                  background: i === highlightIndex ? 'rgba(201,168,76,0.1)' : 'transparent',
                  borderColor: 'rgba(201,168,76,0.08)',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectContact(contact);
                }}
                onMouseEnter={() => setHighlightIndex(i)}
                data-testid={`${testId}-option-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1a1a2e]">
                    {contact.full_name}
                  </span>
                  <div className="flex items-center gap-2">
                    {contact.contact_no && (
                      <span className="text-xs font-mono text-[#6b7280]">{contact.contact_no}</span>
                    )}
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{ background: catStyle.bg, color: catStyle.color }}
                    >
                      {contact.category || 'client'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  {contact.title && (
                    <span className="text-xs text-[#6b7280]">{contact.title}</span>
                  )}
                  {contact.company && (
                    <span className="text-xs text-[#9ca3af]">{contact.company}</span>
                  )}
                  {contact.email && (
                    <span className="text-xs text-[#9ca3af]">{contact.email}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No results message */}
      {isOpen && !searching && value && value.trim().length >= 1 && results.length === 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg border px-3 py-4 text-center"
          style={{ background: '#f8f7f4', borderColor: 'rgba(201,168,76,0.2)' }}
        >
          <p className="text-sm text-[#6b7280]">No contacts found. Type to enter manually.</p>
        </div>
      )}
    </div>
  );
}
